import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Share2, Copy, Check, Mail, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  whatsappHref, emailHref, copyToClipboard, canNativeShare, nativeShare, recordShare,
} from "@/lib/share";

// Generic share control. Opens a small popover with native share, WhatsApp,
// Email and Copy-link options. Optionally records a growth share event.
export function ShareButton({
  url, title = "Nearfluence", text = "", subject = title,
  recordKind = null, recordId = null,
  label = "Share", variant = "outline", className = "", testid = "share-button", icon = true,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const fire = () => { if (recordKind && recordId) recordShare(recordKind, recordId); };

  const doNative = async () => {
    const res = await nativeShare({ title, text, url });
    if (res === "shared") { fire(); setOpen(false); toast.success("Shared"); }
  };
  const doCopy = async () => {
    const ok = await copyToClipboard(url);
    if (ok) { setCopied(true); fire(); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); }
    else toast.error("Couldn't copy link");
  };
  const doExternal = () => { fire(); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={variant} data-testid={testid} className={`rounded-full ${className}`}>
          {icon && <Share2 className="mr-2 h-4 w-4" />} {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" data-testid={`${testid}-menu`}>
        {canNativeShare() && (
          <button data-testid={`${testid}-native`} onClick={doNative}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-700 hover:bg-zinc-50">
            <Send className="h-4 w-4 text-emerald-600" /> Share via…
          </button>
        )}
        <a data-testid={`${testid}-whatsapp`} href={whatsappHref(text || title, url)} target="_blank" rel="noopener noreferrer" onClick={doExternal}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-700 hover:bg-zinc-50">
          <MessageCircle className="h-4 w-4 text-green-600" /> WhatsApp
        </a>
        <a data-testid={`${testid}-email`} href={emailHref(subject, text || title, url)} onClick={doExternal}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-700 hover:bg-zinc-50">
          <Mail className="h-4 w-4 text-sky-600" /> Email
        </a>
        <button data-testid={`${testid}-copy`} onClick={doCopy}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-zinc-700 hover:bg-zinc-50">
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-zinc-500" />} {copied ? "Copied!" : "Copy link"}
        </button>
      </PopoverContent>
    </Popover>
  );
}
